import React, { useState } from 'react';
import { FaEye, FaEdit } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * Markdown Editor with Preview
 * - Live preview toggle
 * - Syntax highlighting in preview
 * - Split view option
 */
const MarkdownEditor = ({ value, onChange, placeholder }) => {
  const [viewMode, setViewMode] = useState('edit'); // 'edit', 'preview', 'split'

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="bg-gray-100 dark:bg-spooky-bg-light border-b border-gray-300 dark:border-gray-600 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setViewMode('edit')}
            className={`px-3 py-1 rounded text-sm font-medium transition ${
              viewMode === 'edit'
                ? 'bg-white dark:bg-spooky-bg-medium text-gray-900 dark:text-spooky-ghost-white shadow'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-spooky-bg-medium'
            }`}
          >
            <FaEdit className="inline mr-1" />
            Edit
          </button>
          <button
            type="button"
            onClick={() => setViewMode('preview')}
            className={`px-3 py-1 rounded text-sm font-medium transition ${
              viewMode === 'preview'
                ? 'bg-white dark:bg-spooky-bg-medium text-gray-900 dark:text-spooky-ghost-white shadow'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-spooky-bg-medium'
            }`}
          >
            <FaEye className="inline mr-1" />
            Preview
          </button>
          <button
            type="button"
            onClick={() => setViewMode('split')}
            className={`px-3 py-1 rounded text-sm font-medium transition ${
              viewMode === 'split'
                ? 'bg-white dark:bg-spooky-bg-medium text-gray-900 dark:text-spooky-ghost-white shadow'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-spooky-bg-medium'
            }`}
          >
            Split
          </button>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {value?.length || 0} characters
        </span>
      </div>

      {/* Content Area */}
      <div className="bg-white dark:bg-spooky-bg-medium">
        {viewMode === 'edit' && (
          <textarea
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            className="w-full px-4 py-3 font-mono text-sm focus:outline-none dark:bg-spooky-bg-medium dark:text-spooky-ghost-white resize-none"
            rows={20}
          />
        )}

        {viewMode === 'preview' && (
          <div className="px-4 py-3 min-h-[500px] prose dark:prose-invert max-w-none">
            {value ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                }}
              >
                {value}
              </ReactMarkdown>
            ) : (
              <p className="text-gray-400 italic">Nothing to preview</p>
            )}
          </div>
        )}

        {viewMode === 'split' && (
          <div className="grid grid-cols-2 divide-x divide-gray-300 dark:divide-gray-600">
            <textarea
              value={value}
              onChange={handleChange}
              placeholder={placeholder}
              className="px-4 py-3 font-mono text-sm focus:outline-none dark:bg-spooky-bg-medium dark:text-spooky-ghost-white resize-none"
              rows={20}
            />
            <div className="px-4 py-3 prose dark:prose-invert max-w-none overflow-y-auto max-h-[500px]">
              {value ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    code({ node, inline, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline && match ? (
                        <SyntaxHighlighter
                          style={vscDarkPlus}
                          language={match[1]}
                          PreTag="div"
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {value}
                </ReactMarkdown>
              ) : (
                <p className="text-gray-400 italic">Nothing to preview</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarkdownEditor;
